import { Injectable } from '@angular/core';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import { SearchFlagsService } from '@app/search/services';
import { Entity, ReactiveList } from '@dg/shared-rsm';
import { AuthService } from '@dg/shared-services';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  FeaturedItemResult,
  GroupResult,
  OpportunityResult,
  PeopleResult,
  RelatedSkillResult,
  SkillResult,
} from './models';
import {
  RelatedDataTotals,
  SearchRelatedItemsResponse,
} from './models/search-related.model';
import { SearchDataService } from './search.data-service';

/**
 * Inject 'id' keys into RelatedItems; need for entity
 * management in the ReactivList.
 */
const injectItemIds =
  (key: string) => (response: SearchRelatedItemsResponse) => {
    if (response) {
      response.items = response.items?.map((it) => {
        it.id = it[key];
        return it;
      });
    }
    return response;
  };

/**
 * Store to manage 6 reactive lists: skills, opportunities, people, groups, relatedTerms, featuredItems
 * Note: Each list
 *  - is externally loaded,
 *  - loads and tracks status independently,
 *  - tracks selections independently
 *  - publishes VM independently
 */
@Injectable()
export class SearchRelatedFacade {
  public skills = new ReactiveList<SkillResult>('skills');
  public people = new ReactiveList<PeopleResult>('people');
  public groups = new ReactiveList<GroupResult>('groups');
  public opportunities = new ReactiveList<OpportunityResult>('opportunities');
  public relatedSkills = new ReactiveList<RelatedSkillResult>('relatedSkills');
  public featuredItems = new ReactiveList<FeaturedItemResult>('featuredItems');

  private _subscriptions = new Subscription();
  private _searchTerm = '';
  private _orgId = 0;

  constructor(
    private searchDataService: SearchDataService,
    private searchFlagsService: SearchFlagsService,
    private authService: AuthService
  ) {}

  /**
   * Accessor to aggregate counts
   */
  public get totalCounts(): RelatedDataTotals {
    return {
      opportunitiesTotal: this.opportunities.total,
      skillsTotal: this.skills.total,
      peopleTotal: this.people.total,
      groupsTotal: this.groups.total,
    };
  }

  /**
   * Reset all lists and cancel inflight requests
   */
  public reset(all = true) {
    // !!Stop all child stream activity...
    this._subscriptions.unsubscribe();

    if (all) {
      this.skills.reset();
      this.people.reset();
      this.groups.reset();
      this.opportunities.reset();
      this.relatedSkills.reset();
      this.featuredItems.reset();
    }
  }

  /**
   * Load all side bar lists using the current search, filters, and orgId
   */
  public searchWith(
    searchTerm: string,
    orgId: number,
    isMarketplaceCat: boolean = false
  ): void {
    if (!this.isDistinctSearch(searchTerm, orgId)) return;

    // HoF to load each list
    const loadFromTo = (
      requestAPI$: Observable<SearchRelatedItemsResponse>,
      list: ReactiveList<Entity>
    ) => {
      const onLoad = (response: SearchRelatedItemsResponse) => {
        const { items, total } = response;
        list.addItems(items, true, total);
        list.setLoading(false);
      };

      // Add subscription to internal list for easy cancels
      this._subscriptions.add(requestAPI$.subscribe(onLoad));
    };

    // !!Stop all child stream activity...
    this._subscriptions.unsubscribe();
    this._subscriptions.closed = false;

    this._subscriptions = new Subscription();

    const disableSearchRelatedSkills =
      this.authService.authUser?.defaultOrgInfo?.settings
        .disableSearchRelatedSkills;

    // Don't load related skills if disabled
    if (disableSearchRelatedSkills) {
      // hide loading skeleton
      this.relatedSkills.updateStatus('success');
    } else {
      loadFromTo(this.loadRelatedSkills(), this.relatedSkills);
    }

    if (isMarketplaceCat && !searchTerm) {
      loadFromTo(this.loadFeaturedItems(), this.featuredItems);
    }

    // Don't load related data if it has a dedicated tab
    if (!this.searchFlagsService.showSearchLayoutRedesign) {
      loadFromTo(this.loadSkills(), this.skills);
      loadFromTo(this.loadOpportunities(), this.opportunities);
      loadFromTo(this.loadGroups(), this.groups);
      loadFromTo(this.loadPeople(), this.people);
    }
  }

  /**
   * Check to see if this is the same as the last search
   * @returns boolean
   */
  private isDistinctSearch(searchTerm: string, orgId: number): boolean {
    const isDistinct = searchTerm !== this._searchTerm || orgId !== this._orgId;
    this._searchTerm = searchTerm;
    this._orgId = orgId;

    return isDistinct;
  }

  // *****************************************************************
  // Internal Async Request factories
  // *****************************************************************

  private loadRelatedSkills(): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchDataService.loadRelatedSkills(this._searchTerm);

    return request$.pipe(
      map(injectItemIds('term')),
      this.relatedSkills.trackLoadStatus
    );
  }

  private loadPeople(): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchDataService.loadPeople(
      this._searchTerm,
      this._orgId
    );

    return request$.pipe(
      map(injectItemIds('linkUrl')),
      this.people.trackLoadStatus
    );
  }

  private loadGroups(): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchDataService.loadGroups(
      this._searchTerm,
      this._orgId
    );

    return request$.pipe(
      map(injectItemIds('linkUrl')),
      this.groups.trackLoadStatus
    );
  }

  private loadSkills(): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchDataService.loadSkills(
      this._searchTerm,
      this._orgId
    );

    return request$.pipe(
      map(injectItemIds('linkUrl')),
      this.skills.trackLoadStatus
    );
  }

  private loadOpportunities(): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchDataService.loadOpportunities(
      this._searchTerm,
      this._orgId
    );

    return request$.pipe(
      map(injectItemIds('linkUrl')),
      this.opportunities.trackLoadStatus
    );
  }

  private loadFeaturedItems(): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchDataService.loadFeaturedItems();

    return request$.pipe(
      map(injectItemIds('id')),
      this.featuredItems.trackLoadStatus
    );
  }
}
